import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import metricsAPI from './api';
import { useAppTheme } from '../../contexts/ThemeContext';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({});
  const { palette, Colors } = useAppTheme();

  // Semantic chart colors — pulled from Colors so they stay consistent across themes
  const chartColors = {
    primary:  Colors.primary.main,        // emerald — revenue / positive
    pink:     Colors.status.error,        // red     — expenses / vacancy
    green:    Colors.status.success,      // emerald — occupied / collected
    orange:   Colors.status.warning,      // amber   — unpaid bills / warning
    purple:   Colors.accent.purple,       // purple  — expected rent
    blue:     Colors.accent.blue,         // blue    — total units / move-ins
    red:      Colors.status.error,        // red     — overdue
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await metricsAPI.getAllDashboardMetrics();
        setMetrics(data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} sx={{ color: Colors.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const {
    property_metrics,
    financial_metrics,
    tenant_metrics,
    monthly_revenue,
    turnover_data,
    property_type_distribution,
    apartment_performance,
  } = metrics;

  const summaryData = [
    {
      title: 'Total Units',
      value: property_metrics?.total_units || 0,
      details: `${property_metrics?.occupied_units || 0} Occupied / ${property_metrics?.vacant_units || 0} Vacant`,
      icon: <HomeIcon fontSize="large" />,
      color: chartColors.blue,
    },
    {
      title: 'Vacancy Rate',
      value: `${property_metrics?.vacancy_rate || 0}%`,
      details: `${property_metrics?.vacant_units || 0} Vacant / ${property_metrics?.occupied_units || 0} Occupied`,
      icon: <MeetingRoomIcon fontSize="large" />,
      color: chartColors.pink,
    },
    {
      title: 'Unpaid Rent',
      value: `Ksh ${(financial_metrics?.unpaid_rent_balance || 0).toLocaleString()}`,
      details: `Collection Rate: ${financial_metrics?.collection_rate || 0}%`,
      icon: <AccountBalanceWalletIcon fontSize="large" />,
      color: chartColors.red,
    },
    {
      title: 'Unpaid Bills',
      value: `Ksh ${(financial_metrics?.unpaid_bills_balance || 0).toLocaleString()}`,
      icon: <ReceiptIcon fontSize="large" />,
      color: chartColors.orange,
    },
    {
      title: 'Active Tenants',
      value: tenant_metrics?.active_tenants || 0,
      details: `Total: ${tenant_metrics?.total_tenants || 0}`,
      icon: <PeopleIcon fontSize="large" />,
      color: chartColors.green,
    },
    {
      title: 'Expected Rent',
      value: `Ksh ${(financial_metrics?.total_expected_rent || 0).toLocaleString()}`,
      details: `Collected: Ksh ${(financial_metrics?.rent_collected_this_month || 0).toLocaleString()}`,
      icon: <TrendingUpIcon fontSize="large" />,
      color: chartColors.purple,
    },
  ];

  const occupancyData = [
    { name: 'Occupied',    value: property_metrics?.occupied_units    || 0, fill: chartColors.green  },
    { name: 'Vacant',      value: property_metrics?.vacant_units      || 0, fill: chartColors.pink   },
    { name: 'Maintenance', value: property_metrics?.maintenance_units || 0, fill: chartColors.orange },
  ].filter(item => item.value > 0);

  // Shared styles derived from palette
  const cardStyle = {
    backgroundColor: palette.background.secondary,
    border: `1px solid ${palette.border}`,
    borderRadius: '12px',
    color: palette.text.primary,
  };

  const paperStyle = {
    ...cardStyle,
    p: 2.5,
  };

  const chartGridColor   = palette.border;
  const chartLabelColor  = palette.text.secondary;
  const tableHeaderBg    = palette.background.tertiary;
  const tableRowBorderColor = palette.border;

  return (
    <Box sx={{ p: 3, backgroundColor: palette.background.primary, minHeight: '100vh' }}>

      {/* ── Header ─────────────────────────────────────────── */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ ...cardStyle, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ color: Colors.primary.main, fontWeight: 'bold', mb: 0.5 }}>
                Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                Overview of your property management metrics
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Summary Cards ───────────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {summaryData.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
            <Card
              elevation={0}
              sx={{
                ...cardStyle,
                borderTop: `4px solid ${item.color}`,
                height: '100%',
              }}
            >
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 1.5, color: item.color }}>
                  {item.icon}
                </Box>
                <Typography variant="h5" sx={{ color: palette.text.primary, fontWeight: 700 }}>
                  {item.value}
                </Typography>
                <Typography variant="body2" sx={{ color: palette.text.secondary, mt: 0.5 }}>
                  {item.title}
                </Typography>
                {item.details && (
                  <Typography variant="caption" sx={{ color: palette.text.muted, display: 'block', mt: 0.5 }}>
                    {item.details}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Charts ─────────────────────────────────────────── */}
      <Grid container spacing={3}>

        {/* Monthly Revenue & Expenses */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={paperStyle}>
            <Typography variant="h6" sx={{ color: palette.text.primary, mb: 2, fontWeight: 600 }}>
              Monthly Revenue &amp; Expenses
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthly_revenue || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="name" tick={{ fill: chartLabelColor, fontSize: 12 }} />
                <YAxis tick={{ fill: chartLabelColor, fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => `Ksh ${value?.toLocaleString()}`}
                  contentStyle={{ backgroundColor: palette.background.tertiary, border: `1px solid ${palette.border}`, color: palette.text.primary }}
                />
                <Legend wrapperStyle={{ color: chartLabelColor }} />
                <Line type="monotone" dataKey="income"   stroke={chartColors.green} strokeWidth={2} name="Revenue"  dot={{ fill: chartColors.green }} />
                <Line type="monotone" dataKey="expenses" stroke={chartColors.pink}  strokeWidth={2} name="Expenses" dot={{ fill: chartColors.pink }} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Occupancy Status */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={paperStyle}>
            <Typography variant="h6" sx={{ color: palette.text.primary, mb: 2, fontWeight: 600 }}>
              Occupancy Status
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={occupancyData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  labelLine={{ stroke: chartLabelColor }}
                >
                  {occupancyData.map((entry, i) => (
                    <Cell key={`cell-occ-${i}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value} units`}
                  contentStyle={{ backgroundColor: palette.background.tertiary, border: `1px solid ${palette.border}`, color: palette.text.primary }}
                />
                <Legend wrapperStyle={{ color: chartLabelColor }} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Monthly Turnover */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={paperStyle}>
            <Typography variant="h6" sx={{ color: palette.text.primary, mb: 2, fontWeight: 600 }}>
              Monthly Tenant Turnover
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={turnover_data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} />
                <XAxis dataKey="name" tick={{ fill: chartLabelColor, fontSize: 12 }} />
                <YAxis tick={{ fill: chartLabelColor, fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: palette.background.tertiary, border: `1px solid ${palette.border}`, color: palette.text.primary }}
                />
                <Legend wrapperStyle={{ color: chartLabelColor }} />
                <Bar dataKey="moveIn"  name="Move Ins"  fill={chartColors.blue} radius={[4,4,0,0]} />
                <Bar dataKey="moveOut" name="Move Outs" fill={chartColors.pink} radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Property Type Distribution */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={paperStyle}>
            <Typography variant="h6" sx={{ color: palette.text.primary, mb: 2, fontWeight: 600 }}>
              Property Types
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={property_type_distribution || []}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {(property_type_distribution || []).map((entry, index) => (
                    <Cell key={`cell-pt-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => `${value} units`}
                  contentStyle={{ backgroundColor: palette.background.tertiary, border: `1px solid ${palette.border}`, color: palette.text.primary }}
                />
                <Legend wrapperStyle={{ color: chartLabelColor }} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Apartment Performance Table */}
        {apartment_performance && apartment_performance.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={paperStyle}>
              <Typography variant="h6" sx={{ color: palette.text.primary, mb: 2, fontWeight: 600 }}>
                Apartment Performance
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: tableHeaderBg }}>
                      {['Apartment','Location','Total Units','Occupied','Vacancy Rate','Expected Rent','Unpaid Balance'].map(col => (
                        <th
                          key={col}
                          style={{
                            padding: '12px',
                            textAlign: col === 'Apartment' || col === 'Location' ? 'left' : 'center',
                            borderBottom: `1px solid ${tableRowBorderColor}`,
                            color: palette.text.secondary,
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {apartment_performance.map((apt, index) => (
                      <tr
                        key={index}
                        style={{ borderBottom: `1px solid ${tableRowBorderColor}` }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = palette.background.tertiary}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '12px', color: palette.text.primary, fontWeight: 500 }}>{apt.name}</td>
                        <td style={{ padding: '12px', color: palette.text.secondary }}>{apt.location}</td>
                        <td style={{ padding: '12px', textAlign: 'center', color: palette.text.primary }}>{apt.total_units}</td>
                        <td style={{ padding: '12px', textAlign: 'center', color: palette.text.primary }}>{apt.occupied_units}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <span style={{
                            color: apt.vacancy_rate > 20 ? chartColors.red : apt.vacancy_rate > 10 ? chartColors.orange : chartColors.green,
                            fontWeight: 'bold',
                          }}>
                            {apt.vacancy_rate}%
                          </span>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', color: palette.text.primary }}>
                          Ksh {apt.expected_rent?.toLocaleString()}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          <span style={{
                            color: apt.unpaid_balance > 0 ? chartColors.red : chartColors.green,
                            fontWeight: apt.unpaid_balance > 0 ? 'bold' : 'normal',
                          }}>
                            Ksh {apt.unpaid_balance?.toLocaleString()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Paper>
          </Grid>
        )}

      </Grid>
    </Box>
  );
};

export default Dashboard;